// src/components/layout/Footer.js
// ============================================================================
// FOOTER — full on ordinary pages, a slim bar on the drawing screens
// ----------------------------------------------------------------------------
// WHY THIS FILE KNOWS ABOUT DRAWINGS AT ALL
//
// The app shell is `min-h-screen` flex-column: Navbar, then <main flex-grow>,
// then this. On a normal page that is exactly right — the footer sits after the
// content and the page scrolls.
//
// The drawing screens break the assumption. ManageRoomDrawings is
// `h-[calc(100vh-4rem)] overflow-hidden`, i.e. precisely the viewport below the
// navbar. So its bottom edge and the footer's top edge are the same pixel: a
// 300px dark block begins the instant the canvas ends. Two visible consequences,
// both in the screenshots that prompted this:
//
//   • The canvas's own side panels terminate at that seam and read as CUT OFF
//     rather than as ending, because a hard colour change abuts them with no gap.
//   • A user nudging the page to see a little more of a tall cabinet gets a
//     wall of marketing links instead.
//
// THE FIX IS NOT TO DELETE THE FOOTER. Terms, Privacy and Contact have to stay
// reachable from every page. But their FULL three-column form is meant for a
// marketing page, not for a workspace somebody is inside for six hours. So on a
// full-bleed screen it collapses to one slim line carrying the same links, with
// clearance above it.
//
// HOW IT KNOWS: FullBleedLayout already broadcasts its own mount/unmount, and
// Navbar already subscribes to decide its width. This is the second subscriber
// to the same signal — no route list to keep in lockstep, and it covers every
// full-bleed screen (room drawings, item drawings, image library, the order
// drawing workspace) without naming any of them.
//
// WHY A SPACER RATHER THAN A BIGGER MARGIN ON THE FOOTER ITSELF: the gap needs
// the PAGE's background, not the footer's. A margin would leave the dark block's
// edge exactly where it was and simply move it down; a spacer in the page colour
// makes the canvas look like it ends and the page continues, which is what is
// actually happening.
// ============================================================================
import React, { useLayoutEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscribeFullBleed } from './FullBleedLayout.js';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // True while ANY full-bleed screen is mounted. useLayoutEffect (not useEffect)
  // to match Navbar's own subscription, so the footer's form is decided before
  // the browser paints rather than rendering full for one frame and snapping.
  const [fullBleed, setFullBleed] = useState(false);
  useLayoutEffect(() => subscribeFullBleed(setFullBleed), []);

  // ---- slim: workspace screens ----
  if (fullBleed) {
    return (
      <>
        {/* Clearance in the PAGE's colour, so the canvas reads as ending and the
            page as continuing. Without it the side panels appear clipped by the
            dark block rather than simply stopping. */}
        <div className="h-16 bg-gray-50 dark:bg-slate-900 flex-shrink-0" aria-hidden="true" />

        <footer className="bg-gray-800 text-white py-2.5 mt-auto">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-gray-400">
              <span>&copy; {currentYear} Quickbid</span>
              <span className="text-gray-600" aria-hidden="true">|</span>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <span className="text-gray-600" aria-hidden="true">|</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <span className="text-gray-600" aria-hidden="true">|</span>
              <a href="mailto:todd@quickbidpricing.com" className="hover:text-white transition-colors">Contact</a>
              <span className="text-gray-600" aria-hidden="true">|</span>
              <a href="mailto:support@quickbidpricing.com" className="hover:text-white transition-colors">Support</a>
            </div>
          </div>
        </footer>
      </>
    );
  }

  // ---- full: every other page, unchanged ----
  return (
    <footer className="bg-gray-800 text-white py-8 mt-auto">
      <div className="container mx-auto px-4">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-6">

          {/* Company Info */}
          <div>
            <h3 className="text-lg font-bold text-white mb-3">Quickbid</h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              Powerful pricing software built specifically for cabinet shops.
              Price your work accurately, consistently, and profitably.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
              Quick Links
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/pricing"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Pricing
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/register"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Sign Up
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & Contact */}
          <div>
            <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">
              Legal &amp; Contact
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/terms"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <a
                  href="mailto:todd@quickbidpricing.com"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Contact Us
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@quickbidpricing.com"
                  className="text-gray-400 hover:text-white text-sm transition-colors"
                >
                  Support
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700 pt-6">
          {/* Bottom Row */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p className="text-gray-400 text-sm">
              &copy; {currentYear} Quickbid. All rights reserved.
            </p>
            <div className="flex space-x-4 text-sm">
              <Link
                to="/terms"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                to="/privacy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy
              </Link>
              <span className="text-gray-600">|</span>
              <a
                href="mailto:todd@quickbidpricing.com"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;